import React, { useMemo, useState } from 'react';
import { LogisticsMonthData, TimeGranularity } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { StrategyChart, DatasetConfig } from './StrategyChart';
import { requiredStockLinePlugin } from './RequiredStockLinePlugin';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import { Code, ContentCopy } from '@mui/icons-material';
import { ChartControls } from './ChartControls';
import { onHandColorPlugin } from './onHandColorPlugin';
import { useUser } from '../../../hooks/useUser';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';

interface Props {
  logisticsData: LogisticsMonthData[];
  matPrice: number;
  unit: IMassUnitObject;
  onTimeChange: (_time: TimeGranularity) => void;
  materialName: string;
  formulaId: string;
  onMonthSelect: (_index: number) => void;
  selectedIndex?: number;
  terminals?: number[] | null;
}

export const LogisticsInvManagement: React.FC<Props> = ({
  logisticsData,
  matPrice,
  unit,
  onTimeChange,
  materialName,
  formulaId,
  onMonthSelect,
  selectedIndex,
  terminals,
}) => {
  const { data: user } = useUser();
  const [showCalcDialog, setShowCalcDialog] = useState(false);
  const baseCurrency = useBaseCurrency();
  const { data: formulasPricesData } = useFormulasPrices();

  const terminalCount = useMemo(
    () => (terminals || []).length,
    [terminals],
  );

  // Generate debug calculation text
  const calcText = useMemo(() => {
    if (logisticsData.length === 0) return '';
    
    const lines: string[] = [];
    const priceInfo = formulasPricesData?.pricesMap?.[formulaId];
    
    lines.push('LOGISTICS CALCULATION BREAKDOWN');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push(`Formula: ${materialName} (${formulaId})`);
    lines.push('');
    lines.push('PRICING INFORMATION');
    lines.push('='.repeat(50));
    const unitName = unit?.displayName || 'MT';
    if (priceInfo) {
      if (priceInfo.originalCurrency && priceInfo.originalCurrency !== baseCurrency) {
        lines.push(`Original spot price: ${priceInfo.originalPrice?.toLocaleString()} ${priceInfo.originalCurrency}/${unitName}`);
        const fxRate = priceInfo.price / (priceInfo.originalPrice || priceInfo.price);
        lines.push(`FX rate: 1 ${baseCurrency} = ${(1/fxRate).toFixed(4)} ${priceInfo.originalCurrency}`);
        lines.push(`Converted price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName}`);
      } else {
        lines.push(`Spot price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName}`);
      }
      if (formulasPricesData.asOfDate) {
        lines.push(`As of: ${new Date(formulasPricesData.asOfDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
      }
    } else {
      lines.push(`Price: ${matPrice.toLocaleString()} ${baseCurrency}/${unitName} (no spot price data)`);
    }
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    lines.push('Logistics Formula:');
    lines.push('Final Inventory = Initial Storage + Supply - Demand');
    lines.push('Available Inventory = Final Inventory - Buffer (protected)');
    lines.push('Required Quantity = Strategy% × Demand');
    lines.push('Next Initial Storage = Current Final Inventory');
    lines.push('');
    lines.push('='.repeat(50));
    lines.push('');
    
    // Show on-hand storage
    if (logisticsData.length > 0) {
      const onHand = logisticsData[0];
      lines.push('Storage On-Hand:');
      lines.push(`  Current Stock: ${onHand.stock.toLocaleString()} MT`);
      lines.push(`  Buffer (protected): ${onHand.emergencyStock.toLocaleString()} MT`);
      lines.push(`  Available for use: ${onHand.inventory.toLocaleString()} MT`);
      lines.push('');
      lines.push('='.repeat(50));
      lines.push('');
    }
    
    // Skip on-hand entry (first item)
    logisticsData.slice(1).forEach((month) => {
      const initialStorage = month.stock;
      const supply = month.POs;
      const demand = month.expectedDemand;
      const buffer = month.emergencyStock;
      const finalInventory = initialStorage + supply - demand;
      const availableInventory = finalInventory - buffer;
      const requiredQuantity = month.requiredQuantity;
      
      lines.push(`${month.month}:`);
      lines.push(`  Initial Storage: ${initialStorage.toLocaleString()} MT`);
      lines.push(`  Supply (PO's): ${supply.toLocaleString()} MT`);
      lines.push(`  Buffer (protected): ${buffer.toLocaleString()} MT`);
      lines.push(`  Available Calculation: ${initialStorage.toLocaleString()} + ${supply.toLocaleString()} - ${buffer.toLocaleString()} = ${(initialStorage + supply - buffer).toLocaleString()} MT`);
      lines.push(`  Expected Demand (MRP): ${demand.toLocaleString()} MT`);
      lines.push(`  Strategy %: ${month.logisticAmnt}%`);
      lines.push(`  Required Calculation: ${demand.toLocaleString()} × ${month.logisticAmnt}% = ${requiredQuantity.toLocaleString()} MT`);
      lines.push(`  Required Quantity: ${requiredQuantity.toLocaleString()} MT`);
      lines.push(`  Final Calculation: ${initialStorage.toLocaleString()} + ${supply.toLocaleString()} - ${demand.toLocaleString()} = ${finalInventory.toLocaleString()} MT`);
      lines.push(`  Final Inventory: ${finalInventory.toLocaleString()} MT`);
      lines.push('');
    });
    
    return lines.join('\n');
  }, [logisticsData, materialName, formulaId, matPrice, unit, baseCurrency, formulasPricesData]);
  const datasets: DatasetConfig[] = [
    {
      label: 'Inventory',
      dataKey: 'inventory',
      backgroundColor: '#93C5FD',
      borderColor: '#3B82F6',
      borderWidth: 0,
      borderRadius: 4,
      type: 'bar',
    },
    {
      label: 'Expected Consumption (MRP)',
      dataKey: 'visFutOrders',
      borderColor: '#9f9f9f',
      backgroundColor: '#cfcfcf',
      borderRadius: 4,
      type: 'bar',
      fill: true,
      hidden: false,
      residualOf: ['inventory'],
      fullValueKey: 'futureOrders',
    },
    {
      label: 'MRP Demand',
      dataKey: 'expectedDemand',
      backgroundColor: 'transparent',
      borderColor: '#1E3A8A',
      borderWidth: 2,
      borderDash: [5, 5],
      pointRadius: 0,
      type: 'line',
      fill: false,
    },
    {
      label: 'Required quantity',
      dataKey: 'requiredQuantity',
      backgroundColor: 'red',
      borderColor: '',
      borderWidth: 0,
      pointRadius: 0,
      hideFromLegend: false,
      type: 'line',
    },
    {
      label: 'Required Stock',
      dataKey: 'requiredQuantity',
      borderColor: 'red',
      backgroundColor: 'transparent',
      borderWidth: 0,
      type: 'line',
      fill: false,
      hidden: true,
      hideFromLegend: true,
    },
  ];

  // MRP demand line: drawn via plugin so it renders on top of bars
  const mrpValuesRef = React.useRef<number[]>([]);
  const mrpMultiplierRef = React.useRef(1);

  const pluginsFactory = useMemo(
    () => (isQuantity: boolean, matPrice: number) => {
      mrpMultiplierRef.current = isQuantity ? 1 : matPrice;
      mrpValuesRef.current = logisticsData.map(d => (d.expectedDemand || 0) * mrpMultiplierRef.current);

      const mrpLinePlugin = {
        id: 'mrpDemandLine',
        afterDatasetsDraw(chart: any) {
          const { ctx, scales } = chart;
          const xScale = scales.x;
          const yScale = scales.y;
          const values = mrpValuesRef.current;
          if (!values.length || !xScale || !yScale) return;

          const points: { x: number; y: number }[] = [];
          values.forEach((val, i) => {
            const px = xScale.getPixelForValue(i);
            const py = yScale.getPixelForValue(val);
            if (px !== undefined && py !== undefined) {
              points.push({ x: px, y: py });
            }
          });
          if (points.length < 2) return;

          ctx.save();
          ctx.strokeStyle = '#1E3A8A';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.beginPath();
          points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
          ctx.stroke();
          ctx.restore();
        },
      };

      return [mrpLinePlugin, requiredStockLinePlugin, onHandColorPlugin];
    },
    [],
  );

  // Keep MRP values in sync when data changes
  React.useEffect(() => {
    mrpValuesRef.current = logisticsData.map(d => (d.expectedDemand || 0) * mrpMultiplierRef.current);
  }, [logisticsData]);

  return (
    <>
      <StrategyChart
        formulaId={formulaId}
        data={logisticsData}
        matPrice={matPrice}
        unit={unit}
        onTimeChange={onTimeChange}
        datasets={datasets}
        pluginsFactory={pluginsFactory}
        stacked={true}
        showPercentageLabels={false}
        extraParams={{ logistics: true }}
        selectedIndex={selectedIndex != null ? selectedIndex + 1 : undefined}
        onBarClick={(index) => {
          // Subtract 1 to account for on-hand bar at index 0
          const adjustedIndex = Math.max(0, index - 1);
          onMonthSelect(adjustedIndex);
        }}
        renderControls={({ isQuantity, setIsQuantity, timeGranularity, setTimeGranularity, viewMonths, setViewMonths }) => (
          <ChartControls
            title={`${materialName} - Logistics inventory management`}
            isQuantity={isQuantity}
            setIsQuantity={setIsQuantity}
            timeGranularity={timeGranularity}
            setTimeGranularity={setTimeGranularity}
            viewMonths={viewMonths}
            setViewMonths={setViewMonths}
            granularityOptions={terminalCount > 0 && terminalCount < 12 ? ['Month', 'Quarter', 'Terminal', 'Yearly', 'Full'] : ['Month', 'Quarter', 'Yearly', 'Full']}
            extraControls={
              user?.isAdmin && timeGranularity !== 'Month' ? (
                <MuiTooltip title="Show terminal aggregation calculation" arrow placement="top">
                  <button
                    onClick={() => setShowCalcDialog(true)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <Code sx={{ fontSize: 17 }} />
                  </button>
                </MuiTooltip>
              ) : undefined
            }
          />
        )}
    />

      {/* Show Calculation Dialog */}
      <Dialog open={showCalcDialog} onClose={() => setShowCalcDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Logistics Calculation Breakdown</span>
          <IconButton
            size="small"
            onClick={() => {
              try {
                const textToCopy = (window as any).__logisticsTerminalDebugLog || calcText;
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(textToCopy);
                }
              } catch {
                // ignore clipboard errors
              }
            }}
          >
            <ContentCopy sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <pre className="text-xs font-mono whitespace-pre-wrap bg-gray-50 p-4 rounded">
            {(window as any).__logisticsTerminalDebugLog || calcText}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCalcDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
